import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CURATED_SHOPS from '@/mocks/cureatedShops';
import HeroCollage from './components/detail/HeroCollage';
import DesktopCTA from './components/detail/DesktopCTA';
import LookbookSection from './components/detail/LookbookSection';

const shop = CURATED_SHOPS[0];

const heroImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=elegant%20Korean%20groom%20in%20premium%20navy%20bespoke%20suit%20full%20body%20portrait%20soft%20natural%20light%20editorial%20wedding%20photography%20minimal%20warm%20cream%20background%20luxury%20menswear%20quiet%20sophistication%20high-end%20magazine&width=420&height=580&seq=yb-v2-h1&orientation=portrait',
    alt: '빌리언즈테일러 대표 화보',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=premium%20Italian%20wool%20navy%20suit%20fabric%20texture%20detail%20close-up%20soft%20golden%20light%20lapel%20buttonhole%20stitching%20luxury%20menswear%20editorial%20product%20photography%20warm%20ambient&width=400&height=260&seq=yb-v2-h2&orientation=landscape',
    alt: '원단 디테일',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Korean%20wedding%20couple%20romantic%20portrait%20soft%20warm%20hotel%20interior%20lighting%20editorial%20luxury%20wedding%20photography%20elegant%20pose%20cream%20palette&width=260&height=260&seq=yb-v2-h3&orientation=squarish',
    alt: '커플 화보',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Korean%20groom%20fitting%20session%20adjusting%20jacket%20collar%20mirror%20reflection%20soft%20warm%20light%20tailoring%20atelier%20editorial%20wedding%20preparation%20luxury%20menswear%20photography&width=260&height=360&seq=yb-v2-h4&orientation=portrait',
    alt: '피팅 컷',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=luxury%20Korean%20bespoke%20tailoring%20atelier%20interior%20warm%20ambient%20light%20wooden%20shelves%20fabric%20rolls%20minimalist%20elegant%20space%20soft%20cream%20palette%20premium%20menswear%20shop%20editorial%20photography&width=400&height=280&seq=yb-v2-h5&orientation=landscape',
    alt: '아틀리에 공간',
  },
];

const serviceCards = [
  {
    title: '맞춤 예복',
    desc: '촬영 + 본식 포함',
    price: '1,290,000원~',
    icon: 'ri-scissors-line',
  },
  {
    title: '턱시도 대여',
    desc: '촬영용 단기 대여',
    price: '275,000원~',
    icon: 'ri-star-line',
  },
  {
    title: '혼주 예복',
    desc: '가족 패키지 가능',
    price: '890,000원~',
    icon: 'ri-heart-line',
  },
  {
    title: '촬영 패키지',
    desc: '스튜디오 + 촬영 의상',
    price: '650,000원~',
    icon: 'ri-camera-line',
  },
];

export default function YangbokDetailPage() {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: shop.name,
          text: shop.subtitle,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])
  return (
    <div className="min-h-[100dvh] bg-cream pb-24 lg:pb-10">
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-cream-dark">
        <div className="flex items-center h-14 px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
            aria-label="뒤로가기"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h1 className="ml-2 text-sm font-semibold text-navy truncate">
            {shop.name}
          </h1>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setLiked(!liked)}
              className="w-9 h-9 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
              aria-label={liked ? '찜 해제' : '찜하기'}
            >
              <i
                className={`${liked ? 'ri-heart-fill text-rose-400' : 'ri-heart-line'} text-lg`}
              />
            </button>
            <button
              onClick={handleShare}
              className="w-9 h-9 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
              aria-label="공유하기"
            >
              <i className="ri-share-line text-lg" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Hero: full width ── */}
      <div className="pt-3 md:pt-5">
        <HeroCollage images={heroImages} />
      </div>

      {/* ── Content + CTA ── */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="lg:flex lg:gap-10 lg:pt-10">
          {/* Left: brand content */}
          <main className="flex-1 min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Meta */}
              <div className="mt-5 md:mt-6">
                <h2 className="font-serif text-2xl md:text-3xl text-navy">
                  {shop.name}
                </h2>
                <p className="mt-1 text-sm md:text-base text-navy-light">
                  {shop.subtitle}
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs text-navy-light">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-map-pin-line text-softgold" />
                    {shop.area}
                  </span>
                  <span>·</span>
                  <span>실제 후기 {shop.reviews}개</span>
                </div>
              </div>

              {/* 2. Brand Story */}
              <section className="mt-8 md:mt-10">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  Brand Story
                </h3>
                <p className="text-sm md:text-[15px] text-navy leading-[1.8]">
                  {shop.brandIntro}
                </p>
              </section>

              {/* 3. Why This Brand — compact list */}
              <section className="mt-8 md:mt-10">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  Why This Brand
                </h3>
                <div className="bg-white border border-cream-dark rounded-xl p-4 space-y-3">
                  {shop.points.map((p, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-cream flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${p.icon} text-navy text-xs`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-navy">
                          {p.title}
                        </h4>
                        <p className="text-xs text-navy-light leading-relaxed mt-0.5">
                          {p.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 4. Services — horizontal scroll cards */}
              <section className="mt-8 md:mt-10">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  구성 안내
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
                  {serviceCards.map((s, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 w-[180px] snap-start bg-white border border-cream-dark rounded-xl p-4"
                    >
                      <div className="w-9 h-9 rounded-full bg-cream flex items-center justify-center mb-2.5">
                        <i className={`${s.icon} text-navy text-sm`} />
                      </div>
                      <h4 className="font-semibold text-sm text-navy">
                        {s.title}
                      </h4>
                      <p className="text-xs text-navy-light mt-0.5">
                        {s.desc}
                      </p>
                      <p className="text-sm font-serif text-navy mt-2.5">
                        {s.price}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 5. Lookbook — selective preview */}
              <LookbookSection />

              {/* 6. Review Summary */}
              <section className="mt-8 md:mt-10">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  Review Summary
                </h3>
                <div className="bg-white border border-cream-dark rounded-xl p-4">
                  <blockquote className="text-sm md:text-[15px] text-navy italic leading-relaxed font-serif">
                    &ldquo;{shop.reviewSnippet}&rdquo;
                  </blockquote>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[
                      '수정 대응이 꼼꼼해요',
                      '상담이 부담스럽지 않아요',
                      '클래식한 핏 만족도가 높아요',
                    ].map((r) => (
                      <span
                        key={r}
                        className="px-3 py-1.5 bg-cream rounded-full text-xs text-navy font-medium"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              </section>

              {/* 7. FAQ Accordion */}
              <section className="mt-8 md:mt-10">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  FAQ
                </h3>
                <div className="space-y-2">
                  {shop.faq.map((item, i) => (
                    <div
                      key={i}
                      className="bg-white border border-cream-dark rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer"
                      >
                        <span className="text-sm font-medium text-navy">
                          {item.q}
                        </span>
                        <i
                          className={`ri-arrow-down-s-line text-navy transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {openFaq === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                          >
                            <div className="px-4 pb-3.5">
                              <p className="text-xs text-navy-light leading-relaxed">
                                {item.a}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </section>

              {/* 8. Info */}
              <section className="mt-8 md:mt-10 mb-6">
                <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-3">
                  Information
                </h3>
                <div className="bg-white border border-cream-dark rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-softgold mt-0.5" />
                    <div>
                      <p className="text-[11px] text-navy-light mb-0.5">위치</p>
                      <p className="text-sm text-navy">{shop.info.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-time-line text-softgold mt-0.5" />
                    <div>
                      <p className="text-[11px] text-navy-light mb-0.5">운영시간</p>
                      <p className="text-sm text-navy">{shop.info.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-car-line text-softgold mt-0.5" />
                    <div>
                      <p className="text-[11px] text-navy-light mb-0.5">주차</p>
                      <p className="text-sm text-navy">{shop.info.parking}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-calendar-check-line text-softgold mt-0.5" />
                    <div>
                      <p className="text-[11px] text-navy-light mb-0.5">예약 방식</p>
                      <p className="text-sm text-navy">{shop.info.booking}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-softgold mt-0.5" />
                    <div>
                      <p className="text-[11px] text-navy-light mb-0.5">연락처</p>
                      <p className="text-sm text-navy">{shop.info.contact}</p>
                    </div>
                  </div>
                </div>
              </section>
            </motion.div>
          </main>

          {/* Right: sticky CTA — desktop only */}
          <aside className="hidden lg:block w-[340px] shrink-0">
            <DesktopCTA shop={shop} />
          </aside>
        </div>
      </div>

      {/* ── Mobile Sticky Bottom CTA ── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-cream-dark px-4 py-3 z-50">
        <div className="max-w-[1200px] mx-auto flex gap-3">
          <button
            onClick={() => setLiked(!liked)}
            className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border transition-colors cursor-pointer active:scale-90 ${liked ? 'border-rose-300 bg-rose-50' : 'border-cream-dark bg-cream'}`}
            aria-label={liked ? '찜 해제' : '찜하기'}
          >
            <i
              className={`${liked ? 'ri-heart-fill text-rose-400' : 'ri-heart-line text-navy'} text-lg`}
            />
          </button>
          <button className="flex-1 bg-navy text-white font-semibold rounded-xl py-3 text-sm transition-colors hover:bg-navy-light cursor-pointer whitespace-nowrap">
            상담 문의하기
          </button>
          {/* <button className="flex-1 bg-cream text-navy font-medium rounded-xl py-3 text-sm border border-cream-dark transition-colors hover:bg-cream-dark cursor-pointer whitespace-nowrap">
            예약하기
          </button> */}
        </div>
      </div>
    </div>
  );
}